/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';
import {CreateJob, DownloadJob} from '@ion/core/src/types/entities';
import {useDeviceGridSelection, UseDeviceGridSelectionProps} from '../../hooks/useDeviceGridSelection';

export interface UseJobCreateStateProps extends UseDeviceGridSelectionProps {};

/**
 * React Hook to provide selector/state for Job Create functionality.
 *
 *  ```typescript
 *   import {useJobCreateState} from '@ion/applications/src/components/jobs';
 *
 *   const {
 *     selectedDevices,
 *     setSelectedDevices,
 *     totalSelectedDevicesCount,
 *     setTotalSelectedDevicesCount,
 *     areTotalRowsSelected,
 *     setAreTotalRowsSelected,
 *     totalRowsCount,
 *     setTotalRowsCount,
 *     filters,
 *     setFilters,
 *     search,
 *     setSearch,
 *     includeSubAccounts,
 *     setIncludeSubAccounts,
 *     viewSelectDevices,
 *     setViewSelectDevices,
 *     clonedJob,
 *     setClonedJob,
 *     selectedChips,
 *     setSelectedChips,
 *     tasks,
 *     setTasks,
 *     tasksInitialized,
 *     setTasksInitialized,
 *     jobName,
 *     setJobName,
 *     jobDescription,
 *     setJobDescription,
 *     isDevicesLoading,
 *     setDevicesLoading,
 *     isJobNameEmpty,
 *     setIsJobNameEmpty,
 *     isFileNameMissing,
 *     setIsFileNameMissing,
 *     isDuplicateFile,
 *     setIsDuplicateFile,
 *     isTaskGridEmpty,
 *     setIsTaskGridEmpty
 *   } = useJobCreateState();
 *  ```
 */
export const useJobCreateState = ({
  selectedDevices      : originalSelectedDevices,
  areTotalRowsSelected : originalAreTotalRowsSelected,
  totalRowsCount       : originalTotalRowsCount,
  filters              : originalFilters,
  search               : originalSearch,
  includeSubAccounts   : originalIncludeSubAccounts
}: UseJobCreateStateProps) => {
  const [totalSelectedDevicesCount, setTotalSelectedDevicesCount] = useState<number>(0);
  const [viewSelectDevices, setViewSelectDevices]                 = useState<boolean>(false);
  const [clonedJob, setClonedJob]                                 = useState<DownloadJob>(null);
  const [selectedChips, setSelectedChips]                         = useState([]);
  const [tasks, setTasks]                                         = useState<CreateJob>(null);
  const [tasksInitialized, setTasksInitialized]                   = useState<boolean>(false);
  const [jobName, setJobName]                                     = useState<string>(null);
  const [jobNameUpdateEnabled, setJobNameUpdateEnabled]           = useState<boolean>(false);
  const [jobDescription, setJobDescription]                       = useState<string>(null);
  const [isDevicesLoading, setDevicesLoading]                     = useState<boolean>(false);
  const [isJobNameEmpty, setIsJobNameEmpty]                       = useState<boolean>(false);
  const [isFileNameMissing, setIsFileNameMissing]                 = useState<boolean>(false);
  const [isFilesNotFound, setIsFilesNotFound]                     = useState(false);
  const [isDuplicateFile, setIsDuplicateFile]                     = useState<boolean>(false);
  const [isTaskGridEmpty, setIsTaskGridEmpty]                     = useState<boolean>(false);
  const [isJobNameManuallyModified, setJobNameManuallyModified]   = useState<boolean>(false);
  const [isMaxDeviceSelected, setIsMaxDeviceSelected]             = useState<boolean>(false);
  const [filteredDeviceFileTypes, setFilteredDeviceFileTypes]     = useState([]);

  const {
    selectedDevices,
    setSelectedDevices,
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts
  } = useDeviceGridSelection({
    selectedDevices      : originalSelectedDevices,
    areTotalRowsSelected : originalAreTotalRowsSelected,
    totalRowsCount       : originalTotalRowsCount,
    filters              : originalFilters,
    search               : originalSearch,
    includeSubAccounts   : originalIncludeSubAccounts
  });

  return {
    selectedDevices,
    setSelectedDevices,
    totalSelectedDevicesCount,
    setTotalSelectedDevicesCount,
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts,
    viewSelectDevices,
    setViewSelectDevices,
    clonedJob,
    setClonedJob,
    selectedChips,
    setSelectedChips,
    tasks,
    setTasks,
    tasksInitialized,
    setTasksInitialized,
    jobName,
    setJobName,
    jobNameUpdateEnabled,
    setJobNameUpdateEnabled,
    jobDescription,
    setJobDescription,
    isDevicesLoading,
    setDevicesLoading,
    isJobNameEmpty,
    setIsJobNameEmpty,
    isFileNameMissing,
    setIsFileNameMissing,
    isFilesNotFound,
    setIsFilesNotFound,
    isDuplicateFile,
    setIsDuplicateFile,
    isTaskGridEmpty,
    setIsTaskGridEmpty,
    isJobNameManuallyModified,
    setJobNameManuallyModified,
    setIsMaxDeviceSelected,
    isMaxDeviceSelected,
    setFilteredDeviceFileTypes,
    filteredDeviceFileTypes
  };
};

export default useJobCreateState;
